<template>
  <v-form ref="form">
    <v-row justify="space-around">

      <v-col cols="12" md="5">
        <!-- Name -->
        <v-text-field
          outlined
          v-model="model.name"
          :label="$t('common:name')"
        />

        <!-- Client -->
        <v-text-field
          outlined
          v-model="model.client"
          :label="$t('display/offer:edit.client')"
        />

        <!-- Category -->
        <v-select
          :items="categoryOption"
          item-text="name"
          item-value="id"
          :menu-props="{bottom: true, offsetY: true}"
          v-model="model.category"
          outlined
          clearable
          :label="$t('display/offer:create.category')"
        />

        <h2 class="primary--text font-weight-bold pt-5 pl-2">
          {{ $t('display/offer:create.additional-parameters') }}
        </h2>

        <!-- Budget -->
        <v-text-field
          outlined
          v-model="model.budget"
          @input="(V) => model.budget = replaceComma(v)"
          @change="(v) => model.budget = updateCost(v, true)"
          :label="$t('common:budget')"
          :rules="[ruleIsEmptyOrNumberText($t('utils:rules.onlynumerictext'))]"
        />

        <!-- Playouts -->
        <v-text-field
          outlined
          v-model="model.playouts"
          @input="(V) => model.playouts = replaceComma(v)"
          @change="(v) => model.playouts = updateCost(v, false)"
          :label="$t('display/offer:edit.playouts')"
          :rules="[ruleIsEmptyOrNumberText($t('utils:rules.onlynumerictext'))]"
        />

      </v-col>
      <v-col cols="12" md="5">
        <!-- Provision -->
        <v-text-field
          v-if="isAdmin"
          outlined
          append-icon="%"
          v-model="model.provision"
          @change="(v) => model.provision = getCorrectPercentage(v)"
          :label="$t('display/offer:edit.provision')"
        />

        <!-- Discount -->
        <v-text-field
          outlined
          append-icon="%"
          v-model="model.discount"
          @change="(v) => model.discount = getCorrectPercentage(v)"
          :label="$t('display/offer:edit.discount')"
        />

        <h3 class="primary--text font-weight-bold pt-5 pl-2">
          {{ $t('display/offer:create.broadcast-period') }}
        </h3>

        <DateField
          ref="dateStart"
          @update="(v) => updateStartDate(v)"
          :isClearable="true"
          :label="$t('common:start-date')"
        />

        <DateField
          ref="dateEnd"
          @update="(v) => {model.dateEnd = v;}"
          :isClearable="true"
          :label="$t('common:end-date')"
        />
      </v-col>

    </v-row>
  </v-form>
</template>

<script>
import SettingsService from '@/modules/display/services/settings-service';
import {ruleIsEmptyOrNumberText} from '@/modules/core/services/rules';
import DateField from '@/modules/core/components/DateField';
import DateUtils from '@/modules/core/services/date-utils';
import InfoService from "@/modules/core/services/info-service";
import {Role} from "@/modules/core/data/roles";
import Offer from "@/modules/display/data/offer";
import Utils from "@/modules/core/services/utils";

export default {
  name: "FirstStep",
  components: {
    DateField
  },
  props: {
    loggedInCustomer: Object
  },
  data() {
    return {
      categoryOption: [],
      model: new Offer(),
      playoutCost: 0
    }
  },
  computed: {
    isAdmin() {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.includes(Role.Admin);
      }
      return false;
    },

    currentUser() {
      return this.$store.state.auth.user;
    }
  },
  mounted() {
    InfoService.readOptionsByUrl('display/tag/categories',
      (response) => {
        this.categoryOption = response;
      }
    );

    SettingsService.readFromAPI('PLAYOUT_COST',
      (response) => {
        this.playoutCost = response.data.value;
      }
    );
  },
  watch: {
    model: {
      deep: true,
      handler() {
        this.$emit('updateStep1Model', this.model);
      }
    },
    loggedInCustomer() {
      this.setDefaultValues();
    }
  },
  methods: {
    ruleIsEmptyOrNumberText,
    clear() {
      this.setDefaultValues();
    },
    getCorrectPercentage(value) {
      let result = value.replace(',','.');
      if (isNaN(result)) {
        result = '';
      } else if (result < 0) {
        result = 0;
      } else if (result > 100) {
        result = 100;
      }

      return Utils.roundNumber(result);
    },
    setDefaultValues() {
      this.model = new Offer();
      this.model.name = this.getCurrentUserName();

      let today = new Date();
      let dayStart = `${today.getDate().toString().padStart(2, '0')}.${(today.getMonth() + 1).toString().padStart(2, '0')}.${today.getFullYear()}`;
      today.setDate(today.getDate() + 7);
      let dayEnd = `${today.getDate().toString().padStart(2, '0')}.${(today.getMonth() + 1).toString().padStart(2, '0')}.${today.getFullYear()}`;

      let checkDateStart = DateUtils.parseStringToDate(dayStart);
      let checkDateEnd = DateUtils.parseStringToDate(dayEnd);

      if (checkDateStart > checkDateEnd) {
        dayEnd = dayStart;
      }

      if (typeof this.$refs.dateStart.parseDTO !== 'undefined') {
        this.model.dateStart = this.$refs.dateStart.parseDTO(dayStart);
        this.model.dateEnd = this.$refs.dateEnd.parseDTO(dayEnd);
        this.$refs.dateEnd.setMinDate(this.model.dateStart);
      }
    },
    updateCost(value, isBudget) {
      if (typeof value !== 'string') return ;

      value = value.replace(',','.');
      if (isNaN(value) || value * 1 < 0) {
        return value;
      }

      if (!isBudget && !Number.isInteger(value)) {
        this.model.playouts = (value * 1).toFixed(0);
      }

      if (isBudget) {
        this.model.budget = Utils.roundNumber(value);
        this.model.playouts = (this.model.budget / this.playoutCost).toFixed(0);

      } else {
        this.model.playouts = Utils.roundNumber(value);
        this.model.budget = (this.model.playouts * this.playoutCost).toFixed(2);
      }

      return Utils.roundNumber(value);
    },
    replaceComma(value) {
      if (typeof value !== 'string') return ;
      return value.replace(',','.');
    },
    isEmpty(value) {
      let result = true;

      let isValue = !value;
      if (typeof value !== 'undefined' &&
        ((value !== 0 && !isValue) || value === 0)
      ) {
        result = false;
      }

      return result;
    },
    getCurrentUserName() {
      let userName = '';
      if (this.loggedInCustomer !== null && !this.loggedInCustomer.name !== true) {
        userName = this.loggedInCustomer.name;
      }

      return userName;
    },
    couldContinue() {
      return this.$refs.form.validate();
    },
    // there is no possibility to create the interface, and this method looks as unnecessary.
    // It invokes in CreateCampaign component
    previousStep() {
    },
    updateStartDate(startDate) {
      let dateFormat = startDate.indexOf('-') > 0 ? 'yyyy-MM-dd' : process.env.VUE_APP_DATE_FORMAT_DTO;
      let dateStart = DateUtils.parseStringToDate(startDate, dateFormat);
      let dateEnd = DateUtils.parseStringToDate(this.model.dateEnd);

      this.model.dateStart = startDate;
      this.$refs.dateEnd.setMinDate(startDate);

      if (dateStart.getTime() > dateEnd.getTime()) {
        this.$refs.dateEnd.parseDTO(startDate);
        this.model.dateEnd = startDate;
      }
    }
  }
}
</script>
